// src/context/PatientInfo/InfoPat2Context.js
import React, { createContext, useEffect, useState, useCallback, useRef } from "react";
import { useValidToken } from "../../hooks/useValidToken";
import { getPatientMembership } from "../../utils/services/Patients/getPatientMembership";

export const InfoPat2Context = createContext();

function normalizeMembershipPayload(payload) {
  // payload puede venir:
  // - array
  // - objeto
  // - { data: array }
  // - string (ya debería venir parseado por transformResponse, pero igual protegemos)
  if (!payload) return [];

  if (typeof payload === "string") {
    const s = payload.trim();
    if (!s) return [];
    try {
      const parsed = JSON.parse(s);
      return normalizeMembershipPayload(parsed);
    } catch {
      return [];
    }
  }

  if (Array.isArray(payload)) return payload;

  if (payload && typeof payload === "object") {
    if (Array.isArray(payload.data)) return payload.data;
    // si el API devuelve objeto único
    return [payload];
  }

  return [];
}

export const InfoPat2Provider = ({ children }) => {
  const { isValid, authLoading } = useValidToken();

  const canCallApi = useCallback(() => !authLoading && isValid, [authLoading, isValid]);

  // Cache: cipId -> membershipArray
  const cacheRef = useRef(new Map());

  const [membership, setMembership] = useState([]); // ✅ siempre array
  const [membershipCipId, setMembershipCipId] = useState("");

  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipError, setMembershipError] = useState(null);

  const normalizeCip = (cipId) => String(cipId ?? "").trim();

  const loadPatientMembership = useCallback(
    async (cipId, opts = {}) => {
      const normalized = normalizeCip(cipId);
      if (!normalized) {
        console.warn("[InfoPat2Context] Missing cipId for membership call.");
        setMembership([]);
        setMembershipCipId("");
        return [];
      }

      if (!canCallApi()) return [];

      const force = Boolean(opts.force);

      // Cache hit
      if (!force && cacheRef.current.has(normalized)) {
        const cached = cacheRef.current.get(normalized);
        setMembership(cached);
        setMembershipCipId(normalized);
        setMembershipError(null);
        return cached;
      }

      setMembershipLoading(true);
      setMembershipError(null);

      try {
        console.log("[InfoPat2Context] loadPatientMembership cipId:", normalized);

        const raw = await getPatientMembership(normalized);

        console.log("[InfoPat2Context] raw membership response:", raw);

        const arr = normalizeMembershipPayload(raw);

        console.log("[InfoPat2Context] normalized memberships length:", arr.length);

        cacheRef.current.set(normalized, arr);
        setMembership(arr);
        setMembershipCipId(normalized);

        return arr;
      } catch (err) {
        console.error("[InfoPat2Context] Error loading membership:", err);
        setMembershipError(err);
        setMembership([]); // ✅ nunca null
        setMembershipCipId(normalized);
        return [];
      } finally {
        setMembershipLoading(false);
      }
    },
    [canCallApi]
  );

  const clearMembership = useCallback(() => {
    setMembership([]);
    setMembershipCipId("");
    setMembershipError(null);
    setMembershipLoading(false);
  }, []);

  const clearCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  // Si token se invalida: limpias todo
  useEffect(() => {
    if (authLoading) return;
    if (!isValid) {
      clearMembership();
      clearCache();
    }
  }, [authLoading, isValid, clearMembership, clearCache]);

  return (
    <InfoPat2Context.Provider
      value={{
        membership,
        membershipCipId,
        membershipLoading,
        membershipError,
        loadPatientMembership,
        clearMembership,
        clearCache,
      }}
    >
      {children}
    </InfoPat2Context.Provider>
  );
};
